/** Reference data: payment methods (seeded), categories (imported from Excel), settings. */
import { hashSourceRows, type ParsedHierarchy, type SourceRow } from "@/domain/categories/hierarchy";
import { computeCategoryDiff, hasDestructiveChanges, isEmptyDiff, type CategoryDiff } from "@/domain/categories/diff";
import type { Category, CategoryGroup, CostNature, PaymentMethod } from "@/domain/model";
import type { AccountingRule, PaymentMethodKind } from "@/domain/payment";
import type { ReferenceContext } from "@/domain/validation";
import { newId, stableUuid } from "@/lib/hash";
import { nowIso, type BudgetDatabase } from "./db";

// ─── Payment methods ────────────────────────────────────────────────────────────────────────────

interface PaymentMethodSeed {
  id: string;
  name: string;
  kind: PaymentMethodKind;
  accountingRule: AccountingRule;
  sortOrder: number;
}

/**
 * Initial payment methods from the workbook's `Portafoglio Denaro`, with the user's rules
 * (decision D4): only Carta Unicredit is booked in the following month. Fixed ids keep every
 * device consistent for sync packages.
 */
export const DEFAULT_PAYMENT_METHODS: readonly PaymentMethodSeed[] = [
  { id: "pm-contanti", name: "Contanti", kind: "cash", accountingRule: "same_month", sortOrder: 10 },
  { id: "pm-carta-unicredit", name: "Carta Unicredit", kind: "credit_card", accountingRule: "next_month", sortOrder: 20 },
  { id: "pm-postepay", name: "PostePay", kind: "prepaid_card", accountingRule: "same_month", sortOrder: 30 },
  { id: "pm-satispay", name: "Satispay", kind: "wallet", accountingRule: "same_month", sortOrder: 40 },
];

/** Inserts missing default methods; never overwrites rows the user may have edited. */
export async function seedPaymentMethods(db: BudgetDatabase): Promise<number> {
  const now = nowIso();
  let inserted = 0;
  await db.transaction("rw", db.paymentMethods, async () => {
    for (const seed of DEFAULT_PAYMENT_METHODS) {
      if (await db.paymentMethods.get(seed.id)) continue;
      await db.paymentMethods.add({ ...seed, excelLabel: seed.name, isActive: true, createdAt: now, updatedAt: now });
      inserted++;
    }
  });
  return inserted;
}

export async function listPaymentMethods(db: BudgetDatabase, { activeOnly = true } = {}): Promise<PaymentMethod[]> {
  const all = await db.paymentMethods.orderBy("sortOrder").toArray();
  return activeOnly ? all.filter((m) => m.isActive) : all;
}

export async function addPaymentMethod(
  db: BudgetDatabase,
  input: { name: string; kind: PaymentMethodKind; accountingRule: AccountingRule },
): Promise<PaymentMethod> {
  const name = input.name.trim();
  if (!name) throw new Error("Il nome è obbligatorio.");
  if (await db.paymentMethods.where("name").equals(name).first()) throw new Error("Esiste già un metodo con questo nome.");
  const now = nowIso();
  const method: PaymentMethod = {
    id: newId(),
    name,
    kind: input.kind,
    accountingRule: input.accountingRule,
    excelLabel: name,
    sortOrder: 100 + (await db.paymentMethods.count()),
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
  await db.paymentMethods.add(method);
  return method;
}

/** A rule change applies to new records only: saved expenses keep their stored accounting month. */
export async function updatePaymentMethod(
  db: BudgetDatabase,
  id: string,
  patch: Partial<Pick<PaymentMethod, "accountingRule" | "isActive" | "kind">>,
): Promise<void> {
  await db.paymentMethods.update(id, { ...patch, updatedAt: nowIso() });
}

// ─── Categories (read-only mirror of Excel "Elenco Spese") ──────────────────────────────────────

export class CategoryImportBlockedError extends Error {
  override readonly name = "CategoryImportBlockedError";
  constructor(readonly diff: CategoryDiff) {
    super("Il file Excel rimuove o sposta categorie esistenti: serve una conferma esplicita.");
  }
}

export const categoryId = (label: string) => stableUuid("category", label);
export const categoryGroupId = (label: string) => stableUuid("category-group", label);

export interface CategoryImportResult {
  status: "unchanged" | "applied";
  diff: CategoryDiff;
}

/**
 * Imports the hierarchy parsed from Elenco Spese. Ids are derived from the exact labels, so every
 * device (and every re-import) produces the same ids. Additions apply directly; removals and
 * moves need `allowDestructive`. Categories are deactivated, never deleted, and keep the user's
 * fixed/discretionary classification.
 */
export async function importCategories(
  db: BudgetDatabase,
  parsed: ParsedHierarchy,
  sourceRows: SourceRow[],
  source: string,
  { allowDestructive = false }: { allowDestructive?: boolean } = {},
): Promise<CategoryImportResult> {
  const contentSha256 = await hashSourceRows(sourceRows);
  const groups = await db.categoryGroups.toArray();
  const cats = await db.categories.toArray();
  const groupLabel = new Map(groups.map((g) => [g.id, g.excelLabel]));
  const diff = computeCategoryDiff(
    {
      groups: groups.map((g) => ({ label: g.excelLabel, isActive: g.isActive })),
      categories: cats.map((c) => ({ label: c.excelLabel, groupLabel: groupLabel.get(c.groupId) ?? "", isActive: c.isActive })),
    },
    parsed,
  );
  const latest = await db.categoryImports.orderBy("importedAt").last();
  if (isEmptyDiff(diff) && latest?.contentSha256 === contentSha256) return { status: "unchanged", diff };
  if (hasDestructiveChanges(diff) && !allowDestructive) throw new CategoryImportBlockedError(diff);

  const now = nowIso();
  const existingCats = new Map(cats.map((c) => [c.excelLabel, c]));
  const existingGroups = new Map(groups.map((g) => [g.excelLabel, g]));
  const nextGroups: CategoryGroup[] = [];
  const nextCats: Category[] = [];
  for (const g of parsed.groups) {
    const id = await categoryGroupId(g.label);
    const prev = existingGroups.get(g.label);
    nextGroups.push({ id, excelLabel: g.label, sortOrder: g.sortOrder, excelRow: g.excelRow, isActive: true, createdAt: prev?.createdAt ?? now, updatedAt: now });
    for (const leaf of g.leaves) {
      const prevCat = existingCats.get(leaf.label);
      nextCats.push({
        id: await categoryId(leaf.label),
        groupId: id,
        excelLabel: leaf.label,
        sortOrder: leaf.sortOrder,
        excelRow: leaf.excelRow,
        isActive: true,
        costNature: prevCat?.costNature ?? null,
        createdAt: prevCat?.createdAt ?? now,
        updatedAt: now,
      });
    }
  }
  const keepGroups = new Set(nextGroups.map((g) => g.excelLabel));
  const keepCats = new Set(nextCats.map((c) => c.excelLabel));

  await db.transaction("rw", [db.categoryGroups, db.categories, db.categoryImports], async () => {
    await db.categoryGroups.bulkPut(nextGroups);
    await db.categories.bulkPut(nextCats);
    for (const g of groups) if (g.isActive && !keepGroups.has(g.excelLabel)) await db.categoryGroups.update(g.id, { isActive: false, updatedAt: now });
    for (const c of cats) if (c.isActive && !keepCats.has(c.excelLabel)) await db.categories.update(c.id, { isActive: false, updatedAt: now });
    await db.categoryImports.add({
      id: newId(),
      source,
      contentSha256,
      groupCount: parsed.groups.length,
      categoryCount: parsed.leafCount,
      diff,
      warnings: parsed.warnings,
      importedAt: now,
    });
  });
  return { status: "applied", diff };
}

export interface CategoryTreeGroup {
  group: CategoryGroup;
  categories: Category[];
}

export async function getCategoryTree(db: BudgetDatabase, { includeInactive = false } = {}): Promise<CategoryTreeGroup[]> {
  const groups = await db.categoryGroups.orderBy("sortOrder").toArray();
  const cats = await db.categories.orderBy("sortOrder").toArray();
  return groups
    .filter((g) => includeInactive || g.isActive)
    .map((group) => ({ group, categories: cats.filter((c) => c.groupId === group.id && (includeInactive || c.isActive)) }));
}

/** Fixed / discretionary classification — user-configured, never inferred (decision Q5). */
export async function setCostNature(db: BudgetDatabase, id: string, costNature: CostNature | null): Promise<void> {
  const updated = await db.categories.update(id, { costNature, updatedAt: nowIso() });
  if (!updated) throw new Error("Categoria non trovata.");
}

export async function loadReferenceContext(db: BudgetDatabase): Promise<ReferenceContext> {
  const [cats, pms] = await Promise.all([db.categories.toArray(), db.paymentMethods.toArray()]);
  return { categories: new Map(cats.map((c) => [c.id, c])), paymentMethods: new Map(pms.map((p) => [p.id, p])) };
}

// ─── Settings ───────────────────────────────────────────────────────────────────────────────────

export async function getSetting<T>(db: BudgetDatabase, key: string, fallback: T): Promise<T> {
  const row = await db.settings.get(key);
  return row ? (row.value as T) : fallback;
}

export async function setSetting(db: BudgetDatabase, key: string, value: unknown): Promise<void> {
  await db.settings.put({ key, value });
}
